import React, { useState, useEffect } from "react"
import { ReviewerMode } from "./ReviewerMode"
import { lumi } from "../lib/lumi"

interface ReviewableElementProps {
  section: string
  contentKey: string
  elementType: "button" | "image" | "icon" | "link" | "section" | "text"
  children: React.ReactNode
  className?: string
}

export function ReviewableElement({
  section,
  contentKey,
  elementType,
  children,
  className = ""
}: ReviewableElementProps) {
  const [adminMode, setAdminMode] = useState<'editor' | 'reviewer'>(
    (localStorage.getItem('adminMode') as 'editor' | 'reviewer') || 'editor'
  )

  const user = lumi.auth.user
  const isAdmin = user?.userRole === "ADMIN"
  const isReviewer = user?.userRole === "USER"
  
  const canReview = (isAdmin && adminMode === 'reviewer') || (isReviewer && !isAdmin)

  useEffect(() => {
    const handleModeChange = (e: CustomEvent) => {
      setAdminMode(e.detail)
    }
    window.addEventListener('adminModeChange', handleModeChange as EventListener)
    return () => window.removeEventListener('adminModeChange', handleModeChange as EventListener)
  }, [])

  if (!canReview) {
    return <>{children}</>
  }

  const getCurrentValue = () => {
    if (typeof children === 'string') return children
    
    if (React.isValidElement(children)) {
      const childProps = children.props as any
      if (childProps.children && typeof childProps.children === 'string') {
        return childProps.children
      }
      if (childProps.src) return childProps.src
      if (childProps.alt) return childProps.alt
      if (childProps.href) return childProps.href
    }
    
    return `[${elementType}]`
  }

  const displayClass = elementType === 'text' ? 'block' : 'inline-block'
  
  return (
    <div className={`relative ${displayClass} ${className}`}>
      <div className="group relative">
        {children}
        <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
          <ReviewerMode
            section={section}
            contentKey={contentKey}
            currentValue={getCurrentValue()}
            elementType={elementType}
          />
        </div>
      </div>
    </div>
  )
}